import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import Map from '../../components/Map/Map';
import Fader from '../../components/Loaders/Fader';

import { formatNumberWithCommas, formatUnixDate } from '../../../util/HelperFunctions';
import { getRidingInfo, getMessageInfo, getPollInfo, getRidingData } from '../../../http/kyrpAPI';
import { getPollData } from '../../../http/pollsAPI';
import { PARTIES_COLORS, PROVINCIAL_RESULTS } from '../../../util/constants/AppConstants';

import ChevronIcon from '../../../assets/images/icons/chevron.svg';
import CopyIcon from '../../../assets/images/icons/copy.svg';
import StarsIcon from '../../../assets/images/icons/stars.svg';
import './Message.scss';

const Message = () => {
  const config = useSelector((state) => state.config.config);

  const [message, setMessage] = useState(null);
  const [copied, setCopied] = useState(false);
  const [widgets, setWidgets] = useState([]);
  const [pollId, setPollId] = useState(false);
  const [pollData, setPollData] = useState(null);
  const [ridingData, setRidingData] = useState(null);

  const { id } = useParams();

  const navigate = useNavigate();

  const pollIdExists = pollId !== 'NaN';

  const handleGetRidingInfo = async (mppId) => {
    const res = await getRidingInfo(config, mppId);

    if (res.status === 200) {
      setWidgets(res.data.riding.widgets.sort((a, b) => a.id - b.id));
    } else {
      console.log('ERROR: Grabbing Widgets');
    }
  };

  const handleGetPollData = async (pollId) => {
    const res = await getPollData(config, pollId);

    if (res.status === 200) {
      setPollData(res.data.rows[0]);
    } else {
      console.log('ERROR: Grabbing Poll Data');
    }
  };

  const handleGetRidingData = async (ridingId) => {
    const res = await getRidingData(config, ridingId);

    if (res.status === 200) {
      setRidingData(res.data.riding);
    } else {
      console.log('ERROR: Grabbing Riding Data');
    }
  };

  const handleGetPollInfo = async (postalCode) => {
    const res = await getPollInfo(config, postalCode);

    if (res.status === 200) {
      if (!res.data.rows.length || !res.data.rows[0].poll_id) {
        setPollId('NaN');
        return;
      }
      setPollId(res.data.rows[0].poll_id);
      handleGetPollData(res.data.rows[0].poll_id);
      handleGetRidingData(res.data.rows[0].riding_id);
    } else {
      console.log('ERROR: Grabbing Poll information');
    }
  };

  const handleGetMessageInfo = async (id) => {
    const res = await getMessageInfo(config, id);

    if (res.status === 200) {
      setMessage(res.data.message);
      handleGetRidingInfo(res.data.message.mpp_id);
      handleGetPollInfo(res.data.message.postal_code);
    } else {
      console.log('ERROR: Grabbing Message information');
    }
  };

  useEffect(() => {
    if (id && config && config.api) {
      handleGetMessageInfo(id);
    }
  }, [id, config]);

  const checkForEmptyString = (string) => (string === '' ? '-' : string);

  const handleCopy = (value) => {
    if (value !== '') {
      navigator.clipboard.writeText(value).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1000);
      });
    }
  };

  if (!message) {
    return (
      <div className="wrap">
        <Fader />
      </div>
    );
  }

  return (
    <div className="dashboard-components">
      <div className="dc-left full">
        <div className="sentiment-trend chart-component">
          <div className="message">
            <div className="message__header">
              <div className="message__back" onClick={() => navigate('/kyrp')}>
                <img src={ChevronIcon} alt="Back" />
                Back to Messages
              </div>
            </div>
            <div className="message__info">
              <div className="message__status">
                <span className="message__date">{formatUnixDate(message.date_utc, true)}</span>
                <span className="message__category">{message.status}</span>
              </div>
              <div className="message__wrap">
                <div className="message__block">
                  <h2 className="message__title">{message.name}</h2>
                  <p className="message__text">{message.description ? message.description : '-'}</p>
                </div>
                <div className="message__block">
                  <h2 className="message__title">Information</h2>
                  <div className="message__information">
                    <div className="message__line">
                      <span>Address:</span>
                      <span>{checkForEmptyString(message.address)}</span>
                    </div>
                    <div className="message__line">
                      <span>Postal Code:</span>
                      <span>{message.postal_code}</span>
                    </div>
                    <div className="message__line">
                      <span>Email:</span>
                      <span
                        className={`message__email ${message.email === '' ? 'empty' : ''}`}
                        onClick={() => handleCopy(message.email)}
                      >
                        {message.email !== '' && <img src={CopyIcon} alt="Copy" />}
                        {checkForEmptyString(message.email)}
                        {copied && <span className="message__notify">Email copied</span>}
                      </span>
                    </div>
                    <div className="message__line">
                      <span>Phone:</span>
                      <span>{checkForEmptyString(message.phone)}</span>
                    </div>
                    <div className="message__line">
                      <span>Contact Count:</span>
                      <span>{message.contacts}</span>
                    </div>
                  </div>
                  <h2 className="message__title">Category</h2>
                  <span className="message__category">
                    {message.type}
                    {message.subtype && ` — ${message.subtype}`}
                  </span>
                  <h2 className="message__title">Added by</h2>
                  <div className="message__author">
                    {message.staffer_fullname}
                    {message.created_by_ai ? (
                      <div className="message__ai">
                        <img src={StarsIcon} />
                        AI
                      </div>
                    ) : (
                      ''
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="message__demographics">
              <h2 className="message__title">Member notes</h2>
              <p className={`message__text ${message.member_notes !== '' ? 'notes' : ''}`}>
                {message.member_notes !== '' ? message.member_notes : '-'}
              </p>
            </div>
            {widgets.length > 0 && (
              <div className="message__demographics">
                <h2 className="message__title">Know Your Riding — Opportunity Scorecard</h2>
                {(pollIdExists && pollData && ridingData) || (!pollIdExists && ridingData) ? (
                  <div className={`scorecard ${pollIdExists ? 'col3' : 'col2'}`}>
                    {widgets.map((widget, index) => (
                      <div className="scorecard__block">
                        <div className="scorecard__header">
                          <h3 className="scorecard__title">
                            {index + 1}. {widget.name}
                            {widget.id === 4 && ' (Top 3)'}
                          </h3>
                        </div>
                        {widget.id === 1 && (
                          <div className="scorecard__info">
                            <div className="scorecard__line">
                              <span className="scorecard__text">Electoral Unit:</span>
                              {pollIdExists && <span className="scorecard__text">Poll {pollData.poll}</span>}
                              <span className="scorecard__text">{ridingData.riding}</span>
                            </div>
                          </div>
                        )}
                        {widget.id === 2 && (
                          <>
                            <div className="scorecard__info">
                              <div className="scorecard__line">
                                <span className="scorecard__text">Population:</span>
                                {pollIdExists && (
                                  <span className="scorecard__text">
                                    {formatNumberWithCommas(pollData.total_population)}
                                  </span>
                                )}
                                <span className="scorecard__text">
                                  {formatNumberWithCommas(ridingData.total_population)}
                                </span>
                              </div>
                              <div className="scorecard__line">
                                <span className="scorecard__text">Average age:</span>
                                {pollIdExists && (
                                  <span className="scorecard__text">
                                    Male - {pollData.avg_age_male}; Female - {pollData.avg_age_female}
                                  </span>
                                )}
                                <span className="scorecard__text">
                                  Male - {ridingData.avg_age_male}; Female - {ridingData.avg_age_female}
                                </span>
                              </div>
                              <div className="scorecard__line">
                                <span className="scorecard__text">Male / Female ratio:</span>
                                {pollIdExists && (
                                  <div className="scorecard__column">
                                    <div className="chart double">
                                      <div className="chart__labels">
                                        <span className="chart__label">Male ({pollData.male_percentage}%)</span>
                                        <span className="chart__label">Female ({pollData.female_percentage}%)</span>
                                      </div>
                                      <div className="chart__line">
                                        <div
                                          className="chart__progress"
                                          style={{ width: `${pollData.male_percentage}%` }}
                                        ></div>
                                        <div
                                          className="chart__progress"
                                          style={{ width: `${pollData.female_percentage}%` }}
                                        ></div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                <div className="scorecard__column">
                                  <div className="chart double">
                                    <div className="chart__labels">
                                      <span className="chart__label">Male ({ridingData.male_percentage}%)</span>
                                      <span className="chart__label">Female ({ridingData.female_percentage}%)</span>
                                    </div>
                                    <div className="chart__line">
                                      <div
                                        className="chart__progress"
                                        style={{ width: `${ridingData.male_percentage}%` }}
                                      ></div>
                                      <div
                                        className="chart__progress"
                                        style={{ width: `${ridingData.female_percentage}%` }}
                                      ></div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {/* <div className="scorecard__line">
                                <span className="scorecard__text">Visible minority:</span>
                                <span className="scorecard__text">78%</span>
                              </div> */}
                              <div className="scorecard__line">
                                <span className="scorecard__text">Household size:</span>
                                {pollIdExists && (
                                  <span className="scorecard__text">
                                    avg. {pollData.average_household_size} persons
                                  </span>
                                )}
                                <span className="scorecard__text">
                                  avg. {ridingData.average_household_size} persons
                                </span>
                              </div>
                              {/* <div className="scorecard__line">
                                <span className="scorecard__text">Home ownership:</span>
                                <span className="scorecard__text">65% own; 35% rent</span>
                              </div> */}
                              {/* <div className="scorecard__line">
                                <span className="scorecard__text">Median income:</span>
                                <span className="scorecard__text">$82,000</span>
                              </div> */}
                              <div className="scorecard__line">
                                <span className="scorecard__text">Top occupations:</span>
                                {pollIdExists && (
                                  <div className="scorecard__column">
                                    <span className="scorecard__text">
                                      1. {pollData.top_occupations[0].label} ({pollData.top_occupations[0].percentage}%)
                                    </span>
                                    <span className="scorecard__text">
                                      2. {pollData.top_occupations[1].label} ({pollData.top_occupations[1].percentage}%)
                                    </span>
                                  </div>
                                )}
                                <div className="scorecard__column">
                                  <span className="scorecard__text">
                                    1. {ridingData.top_occupations[0].label} ({ridingData.top_occupations[0].percentage}
                                    %)
                                  </span>
                                  <span className="scorecard__text">
                                    2. {ridingData.top_occupations[1].label} ({ridingData.top_occupations[1].percentage}
                                    %)
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="scorecard__disclaimer">
                              * Data supplied by Esri and ArcGIS from November/December 2024
                            </div>
                          </>
                        )}
                        {widget.id === 3 && (
                          <>
                            <div className="scorecard__info">
                              <div className="scorecard__line">
                                <span className="scorecard__text">Party Affiliation:</span>
                                {pollIdExists && (
                                  <div className="scorecard__column">
                                    {pollData.party_results.map((party, i) => (
                                      <div className="chart" key={i}>
                                        <span className="chart__label">
                                          {party.name} ({party.percentage}%)
                                        </span>
                                        <div className="chart__line">
                                          <div
                                            className="chart__progress"
                                            style={{
                                              width: `${party.percentage}%`,
                                              backgroundColor: `${PARTIES_COLORS.find((elem) => elem.party === party.name).color}`,
                                            }}
                                          ></div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                <div className="scorecard__column">
                                  {ridingData.party_results.map((party, i) => (
                                    <div className="chart" key={i}>
                                      <span className="chart__label">
                                        {party.name} ({party.percentage}%)
                                      </span>
                                      <div className="chart__line">
                                        <div
                                          className="chart__progress"
                                          style={{
                                            width: `${party.percentage}%`,
                                            backgroundColor: `${PARTIES_COLORS.find((elem) => elem.party === party.name).color}`,
                                          }}
                                        ></div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <div className="scorecard__line">
                                <span className="scorecard__text">Total votes:</span>
                                {pollIdExists && (
                                  <span className="scorecard__text">{formatNumberWithCommas(pollData.votes)}</span>
                                )}
                                <span className="scorecard__text">{formatNumberWithCommas(ridingData.votes)}</span>
                              </div>
                            </div>
                            <div className="scorecard__disclaimer">* Data supplied by Elections Ontario from 2025</div>
                          </>
                        )}
                        {widget.id === 4 && (
                          <div className="scorecard__info">
                            <div className="scorecard__column">
                              {PROVINCIAL_RESULTS.map((party, i) => (
                                <div className="chart" key={i}>
                                  <span className="chart__label">
                                    {party.name} ({party.percentage}%)
                                  </span>
                                  <div className="chart__line">
                                    <div
                                      className="chart__progress"
                                      style={{
                                        width: `${party.percentage}%`,
                                        backgroundColor: `${PARTIES_COLORS.find((elem) => elem.party === party.name).color}`,
                                      }}
                                    ></div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <Fader />
                )}
              </div>
            )}
            <div className="message__demographics">
              <h2 className="message__title">Know Your Riding — Poll Map</h2>
              {pollId === 'NaN' && (
                <div className="poll-alert">Postal Code Data processing for specific poll. Check back soon</div>
              )}
              <div className={`map__box ${pollId === 'NaN' ? 'compressed' : ''}`}>
                {pollId && pollIdExists ? <Map pollId={pollId} /> : <Fader />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Message;
